/// <reference lib="webworker" />
import { bin16ToPackedString, packedToBin } from './HashStrings';
import { isFirefox } from '../../../compat';
// Metadata file layout:
// 4 bytes: last access timestamp
// 20 bytes hash + 4 bytes size of the first blob in the data file
// same for the second blob etc
const METADATA_OFFSET = 4;
const HASH_SIZE = 20;
const METADATA_STRIDE = HASH_SIZE + 4;
export const METADATA_SUFFIX = '_metadata';
export const BUCKET_OPEN_FAILED = 'Placeholder value for buckets that failed to open';
// Evict everything older than 3 months
const EVICTION_CUTOFF = 3 * 30 * 24 * 60 * 60 * 1000;
// Don't evict anything newer than 15 minutes to avoid cache thrashing, e.g. when a single model is filling up the quota
const EVICTION_RECENT_CUTOFF = 15 * 60 * 1000;
/** OPFS directory shared by the OTG geometry, property database and VDS caches. */
export const OTG_CACHE_DIRECTORY = 'otg_cache';
/** OPFS directory holding the RCS point cloud caches. */
export const RCS_CACHE_DIRECTORY = 'rcs_cache';
/**
 * Every directory an OPFSCache instance may own. **Add new cache directories here**, otherwise
 * they are invisible to quota-induced eviction (see {@link OPFSCache.evictAcrossCaches}).
 *
 * The storage quota is granted per origin, not per directory, so caches in different directories
 * compete for one budget while each can only delete its own files. Without this list, whichever
 * cache happens to hit the wall first would discard its own still-useful data to make room for
 * bytes another cache wrote — and, if the imbalance is large enough, would not free enough to
 * complete the write either.
 *
 * Participation is symmetric: a cache whose directory is absent from this list evicts from itself
 * only, so it can never take from siblings that are unable to take from it.
 */
const CACHE_DIRECTORIES = [OTG_CACHE_DIRECTORY, RCS_CACHE_DIRECTORY];
const WRITE_LOCK_PREFIX = 'opfs-cache-write-lock-';
const QUOTA_EXCEEDED_ERROR = 'QuotaExceededError'; // from https://webidl.spec.whatwg.org/#quotaexceedederror
// When the underlying OS call of getSize() fails, Chromium returns -1 (int64) which WebIDL casts
// to unsigned 0xFFFFFFFFFFFFFFFF, arriving in JS as this double value.
const INVALID_DATA_SIZE = 18446744073709552000;
async function getFiles(dir) {
    const results = [];
    for await (const [key, handle] of dir) {
        if (handle.kind === 'file') {
            results.push(handle.getFile().then((file) => [key, file]));
        }
    }
    return Promise.all(results);
}
export function makeFilenameSafe(s) {
    // List of disallowed characters comes from https://stackoverflow.com/questions/1976007/what-characters-are-forbidden-in-windows-and-linux-directory-names
    return s.replace(/<|>|:|"|\/|\\|\||\?\*/g, '_');
}
/**
 * Implements an asset cache using the Origin Private File System (OPFS).
 * It has arbitrarily many buckets which are identified by arbitrary names. Each bucket is stored as two files.
 * The first file contains only the concatenated asset blobs, the second file contains metadata, i.e. hashes and sizes of the blobs.
 * @class
 */
export class OPFSCache {
    #analyticsCallback;
    #quotaExceededAnalyticsCallbackExecuted = false;
    #initPromise;
    #evictPromise;
    #cacheDir = null;
    #buckets = new Map();
    #initializingBuckets = new Map();
    #cacheDirectoryName = '';
    /** Creates a new OPFSCache instance. Creates the cache directory if it doesn't exist yet. */
    constructor(analyticsCallback, cacheDirectoryName = OTG_CACHE_DIRECTORY) {
        this.#cacheDirectoryName = cacheDirectoryName;
        this.#initPromise = this.#init();
        this.#analyticsCallback = analyticsCallback;
    }
    async #init() {
        try {
            // this throws on firefox and safari in private browsing mode
            const root = await navigator.storage.getDirectory();
            // this throws when the quota is exceeded
            this.#cacheDir = await root.getDirectoryHandle(this.#cacheDirectoryName, { create: true });
        }
        catch (e) {
            console.warn('Failed to open OPFS cache directory', e);
            this.#analyticsCallback?.('viewer.opfsCache.cacheOpenFailed', {
                errorName: e.name,
                errorMessage: e.message,
                ...(await navigator.storage.estimate().catch(() => undefined)),
            });
        }
        this.#initPromise = undefined;
    }
    async open(bucketName) {
        const bucket = this.#buckets.get(bucketName);
        if (bucket) {
            return bucket;
        }
        let p = this.#initializingBuckets.get(bucketName);
        if (p) {
            return p;
        }
        p = this.#openImpl(bucketName, true);
        this.#initializingBuckets.set(bucketName, p);
        await p;
        this.#initializingBuckets.delete(bucketName);
        return p;
    }
    async #openImpl(bucketName, canRetry) {
        let dataAccessHandle, metadataAccessHandle;
        let bucket;
        const bucketFileName = makeFilenameSafe(bucketName);
        const writeLockName = WRITE_LOCK_PREFIX + bucketFileName;
        try {
            await this.#initPromise;
            if (!this.#cacheDir) {
                throw new Error('OPFS cache directory not initialized');
            }
            const dataDraftHandle = await this.#cacheDir.getFileHandle(bucketFileName, { create: true });
            dataAccessHandle = await dataDraftHandle.createSyncAccessHandle({ mode: 'readwrite-unsafe' });
            const metadataDraftHandle = await this.#cacheDir.getFileHandle(bucketFileName + METADATA_SUFFIX, {
                create: true,
            });
            metadataAccessHandle = await metadataDraftHandle.createSyncAccessHandle({ mode: 'readwrite-unsafe' });
            const timestampBuffer = new Uint32Array(1);
            // Update the file's lastModified value to avoid eviction
            timestampBuffer[0] = new Date().valueOf();
            metadataAccessHandle.write(timestampBuffer, { at: 0 });
            bucket = {
                dataHandle: dataAccessHandle,
                offsets: new Map(),
                metadataHandle: metadataAccessHandle,
            };
            // acquire writeLock
            await new Promise((resolve) => {
                navigator.locks.request(writeLockName, { ifAvailable: true, mode: 'exclusive' }, (lock) => {
                    resolve();
                    if (!lock) {
                        return;
                    }
                    // https://developer.mozilla.org/en-US/docs/Web/API/Web_Locks_API#advanced_use
                    return new Promise((resolve) => {
                        bucket.writeLock = resolve;
                    });
                });
            });
            // this.#buckets must be set after the writeLock await above.
            // otherwise #storeInner might see an open bucket before it has writeLock set.
            this.#buckets.set(bucketName, bucket);
            // Read metadata and file size sanity check
            const expectedDataSize = this.#loadMetadata(bucket);
            const actualDataSize = bucket.dataHandle.getSize();
            if (expectedDataSize !== actualDataSize || actualDataSize === INVALID_DATA_SIZE) {
                this.#analyticsCallback?.('viewer.opfsCache.dataSizeMismatch', {
                    bucketName,
                    expectedDataSize,
                    actualDataSize,
                    ...(await navigator.storage.estimate().catch(() => undefined)),
                });
                console.warn('Data file has unexpected size, clearing OPFS cache for', bucketName);
                bucket.offsets.clear();
                bucket.dataHandle.truncate(0);
                bucket.metadataHandle.truncate(METADATA_OFFSET);
            }
            // console.debug('Opened OPFS cache bucket', bucketName, 'with', bucket.offsets.size, 'entries');
            return bucket;
        }
        catch (error) {
            dataAccessHandle?.close();
            metadataAccessHandle?.close();
            bucket?.writeLock?.();
            if (error.name === QUOTA_EXCEEDED_ERROR && canRetry) {
                this.#sendQuotaExceededAnalytics();
                // Creating the bucket files ran into the shared origin budget, so the bytes to
                // reclaim may belong to any cache, exactly as in #storeInner.
                await this.#evict(0.1, false, true);
                return this.#openImpl(bucketName, false);
            }
            // #cacheDir is set once in #init() and kept across bucket open/close cycles —
            // close() only clears #buckets, not the directory handle. If the on-disk folder
            // was removed externally (another tab, clear storage, test cleanup, etc.) while
            // this instance still holds the old handle, getFileHandle() throws NotFoundError.
            // Re-acquire the directory (recreating it if needed) and retry the bucket open once.
            if (error.name === 'NotFoundError' && canRetry) {
                this.#initPromise ??= this.#init();
                await this.#initPromise;
                return this.#openImpl(bucketName, false);
            }
            if (error.name === 'NoModificationAllowedError') {
                console.warn(error.message);
                console.warn(`Failed to open OPFS cache bucket for ${bucketName}. It is probably open in another tab. Disabling cache.`);
            }
            else {
                console.warn('Failed to initialize OPFS cache bucket', bucketName, error);
            }
            // cache dir open failures have their own event above
            if (this.#cacheDir) {
                this.#analyticsCallback?.('viewer.opfsCache.bucketOpenFailed', {
                    bucketName,
                    errorName: error.name,
                    errorMessage: error.message,
                    ...(await navigator.storage.estimate().catch(() => undefined)),
                });
            }
            this.#buckets.set(bucketName, BUCKET_OPEN_FAILED);
            return BUCKET_OPEN_FAILED;
        }
    }
    #loadMetadata(bucket) {
        bucket.offsets.clear();
        const rawSize = bucket.metadataHandle.getSize();
        if (rawSize === INVALID_DATA_SIZE) {
            return -1;
        }
        const metadataSize = rawSize - METADATA_OFFSET;
        // The metadata file always holds a whole number of fixed-size entries, so a remainder means the
        // file is corrupt, e.g. because a write was truncated by a crash or a file system error.
        // Returning -1 makes the size check in #openImpl fail, which clears the bucket.
        if (metadataSize % METADATA_STRIDE !== 0) {
            return -1;
        }
        const data = new Uint8Array(metadataSize);
        const data16 = new Uint16Array(data.buffer);
        const data32 = new Uint32Array(data.buffer);
        bucket.metadataHandle.read(data, { at: METADATA_OFFSET });
        const stride = METADATA_STRIDE;
        let currentDataOffset = 0;
        for (let offset = 0; offset < data.byteLength; offset += stride) {
            const hash = bin16ToPackedString(data16, offset / 2);
            const size = data32[(offset + HASH_SIZE) / 4];
            bucket.offsets.set(hash, [currentDataOffset, size]);
            currentDataOffset += size;
        }
        return currentDataOffset;
    }
    /**
     * True when `bucket` is still the live entry in `#buckets`. After an `await` in store/get,
     * `deleteBucket` or `close` may have removed or replaced that entry while this handler
     * still holds the old object — skip IO rather than use a stale sync access handle.
     */
    #isBucketActive(bucketName, bucket) {
        return this.#buckets.get(bucketName) === bucket;
    }
    /**
     * Stores data associated with the given hash. Attempts one round of cache eviction if the quota is exceeded.
     * Note this does not check whether the hashes are already cached. It will ignore requests for buckets that are not open yet.
     * @param {string[]} hashes - 20 byte hashes of datas
     * @param {string[]} bucketNames - Names of the buckets in which the hashes should be stored. Must have same length as hashes.
     * @param {Uint8Array[]} datas - Array of data to store
     * @throws - Any error thrown by the underlying write function except QuotaExceededError
     */
    async store(hashes, bucketNames, datas) {
        // split hashes, bucketNames and datas by bucketName
        const buckets = new Map();
        for (let i = 0; i < hashes.length; i++) {
            const bucketName = bucketNames[i];
            let bucket = buckets.get(bucketName);
            if (!bucket) {
                bucket = [[], []];
                buckets.set(bucketName, bucket);
            }
            bucket[0].push(hashes[i]);
            bucket[1].push(datas[i]);
        }
        const promises = [];
        for (const [bucketName, [hashes, datas]] of buckets) {
            promises.push(this.#storeInner(hashes, bucketName, datas));
        }
        return (await Promise.all(promises)).every((success) => success);
    }
    async #storeInner(hashes, bucketName, datas) {
        // Collect data into two buffers for data and metadata
        // This is done first because if we go async (opening/eviction),
        // we must have copied the buffers before because the caller might transfer them.
        // Below we might find the bucket unusable, so the work here might be for nothing,
        // but that's a rare case and acceptable.
        const lengths = datas.map((d) => d.length); // lengths become 0 on transfer
        const metadataBuffer = new Uint8Array(hashes.length * METADATA_STRIDE);
        const metadataBuffer32 = new Uint32Array(metadataBuffer.buffer);
        const dataBuffer = new Uint8Array(lengths.reduce((acc, d) => acc + d, 0));
        let dataBufferOffset = 0;
        for (let i = 0; i < hashes.length; i++) {
            const data = datas[i];
            packedToBin(hashes[i], metadataBuffer, i * METADATA_STRIDE);
            metadataBuffer32[(i * METADATA_STRIDE + HASH_SIZE) / 4] = data.length;
            dataBuffer.set(data, dataBufferOffset);
            dataBufferOffset += data.length;
        }
        // The await here before doing IO below has the additional benefit of letting the caller
        // continue processing (e.g. send the data to the decoder threads) earlier.
        const bucket = await this.open(bucketName);
        if (!bucket || bucket === BUCKET_OPEN_FAILED || !bucket.writeLock) {
            return false;
        }
        if (!this.#isBucketActive(bucketName, bucket)) {
            return false;
        }
        // Write the buffers
        if (!this.#writeBatchWithRollback(bucket, dataBuffer, metadataBuffer)) {
            // The quota is exhausted for the whole origin, so evict by age across all caches rather
            // than only this one: the bytes crowding us out may well belong to a different cache,
            // and taking them from here would discard fresher data and might not free enough.
            await this.#evict(0.1, false, true);
            if (!this.#isBucketActive(bucketName, bucket)) {
                return false;
            }
            if (!this.#writeBatchWithRollback(bucket, dataBuffer, metadataBuffer)) {
                return false;
            }
        }
        // Update the in-memory index
        let offset = bucket.dataHandle.getSize() - dataBuffer.length;
        for (let i = 0; i < hashes.length; i++) {
            bucket.offsets.set(hashes[i], [offset, lengths[i]]);
            offset += lengths[i];
        }
        return true;
    }
    /**
     * Appends data and metadata to the bucket. Tries to roll back writes on any error.
     * @param {Bucket} bucket - Bucket to write to
     * @param {Uint8Array} dataBuffer - Data to write
     * @param {Uint8Array} metadataBuffer - Metadata to write
     * @returns {boolean} - true, if data was written successfully, false if the quota was exceeded
     * @throws - any error thrown by the underlying write function except QuotaExceededError
     */
    #writeBatchWithRollback(bucket, dataBuffer, metadataBuffer) {
        const dataSize = bucket.dataHandle.getSize();
        const metadataSize = bucket.metadataHandle.getSize();
        try {
            this.#write(bucket.dataHandle, dataBuffer, { at: dataSize });
            this.#write(bucket.metadataHandle, metadataBuffer, { at: metadataSize });
        }
        catch (error) {
            // Every error could come with a partial write, so we try to roll it back.
            // In theory, truncating can also fail, but we can't do anything about it. The next cache open will clear the cache in that case.
            bucket.dataHandle.truncate(dataSize);
            bucket.metadataHandle.truncate(metadataSize);
            if (error.name !== QUOTA_EXCEEDED_ERROR) {
                throw error;
            }
            this.#sendQuotaExceededAnalytics();
            return false;
        }
        return true;
    }
    /**
     * Wrapper around FileSystemSyncAccessHandle.write to turn partial writes into an exception and to handle a chrome bug
     */
    #write(handle, data, options) {
        const bytesWritten = handle.write(data, options);
        // Chrome bug workaround: When exceeding the quota in incognito mode, it does a partial write,
        // throws no QuotaExceededError, and returns something like 0xFFFFFF00, maybe an internal error code.
        // Not having this code here and just throwing a generic exception like below would work,
        // but turning this into a QuotaExceededError allows playwright tests to continue using incognito mode.
        if (bytesWritten > data.length) {
            throw new DOMException('Quota Exceeded in Chrome Incognito mode', QUOTA_EXCEEDED_ERROR);
        }
        // The spec allows partial writes, see https://fs.spec.whatwg.org/#api-filesystemsyncaccesshandle-write.
        // We'll handle that as an error.
        // Note that when `write` throws some other error, it could also have performed a partial write.
        if (bytesWritten !== data.length) {
            // Firefox does a partial write instead of throwing QuotaExceededError when the quota is full.
            if (isFirefox()) {
                throw new DOMException('Partial write detected in Firefox (likely quota exceeded)', QUOTA_EXCEEDED_ERROR);
            }
            throw new DOMException('Partial write detected');
        }
    }
    /** Retrieves data associated with the given hashes.
     * @param {string[]} hashes - Array of 20 byte hashes of the data
     * @param {string[]} bucketNames - Names of the buckets in which the hashes should be looked up. Must have same length as hashes.
     * @returns {(Uint8Array|null)[]} Array of data associated with the hashes. If a hash is not found in the specified bucket, the array contains null instead.
     */
    async get(hashes, bucketNames) {
        const result = new Array(hashes.length).fill(null);
        for (let i = 0; i < hashes.length; i++) {
            const hash = hashes[i];
            const bucketName = bucketNames[i];
            const bucket = this.#buckets.get(bucketName) || (await this.open(bucketName));
            if (!bucket || bucket === BUCKET_OPEN_FAILED) {
                continue;
            }
            if (!this.#isBucketActive(bucketName, bucket)) {
                continue;
            }
            const fileOffset = bucket.offsets.get(hash);
            if (!fileOffset) {
                continue;
            }
            const data = new Uint8Array(fileOffset[1]);
            bucket.dataHandle.read(data, { at: fileOffset[0] });
            result[i] = data;
        }
        return result;
    }
    /**
     * Removes data from the cache to make room for new.
     * Always removes everything with an LRU-date older than EVICTION_CUTOFF, and then tries to remove more if necessary to hit minFraction.
     * @param {number} minFraction - Minimal fraction of data that should to be removed. E.g. 0.1 means at least 10% of the cache should be evicted.
     * @param {boolean} ignoreRecentCutoff - If true, recently-written buckets are eligible for eviction too.
     * @param {boolean} evictAcrossCaches - If true, the sibling cache directories in {@link CACHE_DIRECTORIES}
     *   take part as one pool: `minFraction` applies to their combined size and the oldest buckets go first
     *   regardless of which directory they live in. Only appropriate for quota-induced eviction, where the
     *   pressure is on the shared origin budget rather than on this cache specifically.
     * @returns {boolean} True if the requested amount of data or more was evicted from the cache
     */
    async #evict(minFraction = 0.1, ignoreRecentCutoff = false, evictAcrossCaches = false) {
        if (this.#evictPromise) {
            const inFlightResult = await this.#evictPromise;
            // eviction already running. quota-induced evictions stop here.
            // user-invoked evictions continue so ignoreRecentCutoff is applied.
            if (!ignoreRecentCutoff) {
                return inFlightResult;
            }
        }
        this.#evictPromise = this.#evictImpl(minFraction, ignoreRecentCutoff, evictAcrossCaches);
        const result = await this.#evictPromise;
        this.#evictPromise = undefined;
        return result;
    }
    /**
     * Handles of the cache directories eviction may delete from, this cache's own directory first.
     * Directories are never created here: one that does not exist holds nothing to evict.
     *
     * A directory absent from {@link CACHE_DIRECTORIES} is invisible to every other cache's eviction,
     * so it stays confined to its own files instead of taking from siblings that cannot take from it.
     */
    async #getEvictionDirectories(evictAcrossCaches) {
        const own = this.#cacheDir;
        if (!evictAcrossCaches || !CACHE_DIRECTORIES.includes(this.#cacheDirectoryName)) {
            return [own];
        }
        let root;
        try {
            root = await navigator.storage.getDirectory();
        }
        catch (e) {
            return [own];
        }
        const directories = [own];
        for (const name of CACHE_DIRECTORIES) {
            if (name === this.#cacheDirectoryName) {
                continue;
            }
            try {
                directories.push(await root.getDirectoryHandle(name, { create: false }));
            }
            catch (e) {
                // NotFoundError: that cache was never used in this origin.
            }
        }
        return directories;
    }
    async #evictImpl(minFraction, ignoreRecentCutoff, evictAcrossCaches) {
        await this.#initPromise;
        if (!this.#cacheDir) {
            return false;
        }
        // Buckets are tracked with the directory they live in so they can be deleted through the
        // right handle once the candidates from all directories are merged into one LRU order.
        const files = new Array();
        let totalSize = 0;
        let readAnyDirectory = false;
        for (const dir of await this.#getEvictionDirectories(evictAcrossCaches)) {
            let dirFiles;
            try {
                dirFiles = await getFiles(dir);
            }
            catch (e) {
                // The cache directory was removed externally (e.g. OPFS cleared mid-session); nothing to evict.
                continue;
            }
            readAnyDirectory = true;
            for (const [, file] of dirFiles) {
                totalSize += file.size;
                if (!file.name.endsWith(METADATA_SUFFIX)) {
                    continue;
                }
                files.push([dir, file]);
            }
        }
        if (!readAnyDirectory) {
            return false;
        }
        // When several caches take part, the target is a fraction of their combined size, because the
        // headroom has to be won back from the shared origin quota rather than from this cache alone.
        // The consequence is that a small cache sitting next to a much larger sibling can lose most of
        // its cold data in a single quota event, since the target may exceed its own total. That is
        // accepted: the alternative — sizing the target from the caller's directory only — regularly
        // fails to free enough to complete the write when the sibling is the one filling the disk.
        const minBytes = totalSize * minFraction;
        // We do write a last access timestamp into the files, but we use the .lastModified property here,
        // which should be equivalent and doesn't require opening the file. We still have to write *something*
        // to the file to update lastModified though as there is no touch API, and a timestamp seemed only fitting.
        const filesSorted = files.sort((a, b) => a[1].lastModified - b[1].lastModified);
        let deletedBytes = 0;
        const now = Date.now();
        const cutoff = now - EVICTION_CUTOFF;
        const recentCutoff = now - EVICTION_RECENT_CUTOFF;
        for (const [dir, metadataFile] of filesSorted) {
            const expired = metadataFile.lastModified <= cutoff;
            const recent = !ignoreRecentCutoff && metadataFile.lastModified > recentCutoff;
            if (!expired && (deletedBytes >= minBytes || recent)) {
                continue;
            }
            try {
                await dir.removeEntry(metadataFile.name);
                deletedBytes += metadataFile.size;
                const bucketFileName = metadataFile.name.slice(0, -METADATA_SUFFIX.length);
                const dataFile = await (await dir.getFileHandle(bucketFileName)).getFile();
                await dir.removeEntry(bucketFileName);
                deletedBytes += dataFile.size;
            }
            catch (e) {
                // Buckets held open by this cache or by another worker's cache throw this. Skipping
                // them is what keeps cross-cache eviction safe: an in-use bucket is never deleted
                // out from under its owner.
                if (e.name !== 'NoModificationAllowedError') {
                    console.warn('Error during cache eviction', e);
                }
            }
        }
        return deletedBytes >= minBytes;
    }
    /** Closes all open file handles. Since the handles are exclusive, this should be called as soon as possible.
     * Note that calling this while initialization is running will make this asynchronous,
     * and attempting any other operation while the close is in progress might break things.
     * Also, calling close while e.g. an asynchronous store is in progress will probably break.
     */
    async close() {
        this.#initPromise && (await this.#initPromise);
        this.#initializingBuckets.size && (await Promise.all(this.#initializingBuckets.values()));
        for (const bucket of this.#buckets.values()) {
            if (!bucket || bucket === BUCKET_OPEN_FAILED) {
                continue;
            }
            this.#closeBucket(bucket);
        }
        this.#buckets.clear();
        await this.#evict(0.0);
    }
    /**
     * Flushes and closes both file handles of a bucket and releases its write lock.
     *
     * A handle that was already closed (e.g. by a concurrent close()) throws InvalidStateError from
     * both flush() and close(). Neither may escape: the write lock is released in a finally so a
     * failure here cannot leave the lock held for the rest of the session, which would block every
     * other tab from opening this bucket.
     */
    #closeBucket(bucket) {
        try {
            for (const handle of [bucket.dataHandle, bucket.metadataHandle]) {
                if (!handle) {
                    continue;
                }
                try {
                    handle.flush();
                    handle.close();
                }
                catch (e) {
                    console.warn('Failed to close OPFS cache file handle', e);
                }
            }
        }
        finally {
            bucket.writeLock?.();
        }
    }
    /** Deletes all buckets */
    async clear() {
        await this.#evict(1.0, true);
    }
    /**
     * Deletes a single bucket's files from the OPFS directory. Silently ignores missing files.
     * @param {string} bucketName - Name of the bucket to delete
     * @param {boolean} closeOpenHandles - If true, open handles of the bucket are closed before deleting
     * its files, and a bucket open that is still in flight is awaited first. If false, the bucket must
     * not have open handles.
     * @throws {Error} If the bucket is currently open or being initialized and closeOpenHandles is false
     */
    async deleteBucket(bucketName, closeOpenHandles = false) {
        const initInProgress = this.#initializingBuckets.get(bucketName);
        if (initInProgress) {
            if (!closeOpenHandles) {
                throw new Error(`Cannot delete bucket '${bucketName}' while it is being initialized`);
            }
            await initInProgress.catch(() => { });
        }
        const bucket = this.#buckets.get(bucketName);
        if (bucket && bucket !== BUCKET_OPEN_FAILED) {
            if (!closeOpenHandles) {
                throw new Error(`Cannot delete bucket '${bucketName}' with open handles`);
            }
            this.#closeBucket(bucket);
        }
        this.#buckets.delete(bucketName);
        await this.#initPromise;
        if (!this.#cacheDir)
            return;
        const bucketFileName = makeFilenameSafe(bucketName);
        for (const name of [bucketFileName, bucketFileName + METADATA_SUFFIX]) {
            try {
                await this.#cacheDir.removeEntry(name);
            }
            catch (_) { /* file may not exist */ }
        }
    }
    async getStats() {
        await this.#initPromise;
        let entries = 0;
        let dataSize = 0;
        let metadataSize = 0;
        if (this.#cacheDir) {
            for (const [key, file] of await getFiles(this.#cacheDir)) {
                if (key.endsWith(METADATA_SUFFIX)) {
                    metadataSize += file.size;
                    entries += (file.size - METADATA_OFFSET) / METADATA_STRIDE;
                }
                else {
                    dataSize += file.size;
                }
            }
        }
        return {
            entries,
            dataSize,
            metadataSize,
        };
    }
    get cacheDir() {
        return this.#cacheDir;
    }
    #sendQuotaExceededAnalytics() {
        if (this.#quotaExceededAnalyticsCallbackExecuted) {
            return;
        }
        this.#quotaExceededAnalyticsCallbackExecuted = true;
        navigator.storage
            .estimate()
            .then((estimate) => {
            this.#analyticsCallback?.('viewer.opfsCache.quotaExceeded', estimate);
        })
            .catch((error) => {
            console.error('Failed to get storage estimate', error);
        });
    }
}
